import React from 'react'
import styled from '@emotion/styled'
import {
  AppVersion,
  MajorVersion as OSMajorVersion,
  isReactNativeApp as isNativeApp,
  isReactNativeAndroid,
  isNativeAppMajorVersionAbove as isNativeVersionAbove,
} from '@mobi/web-native-comms/web'
import { Icon } from '@mobi/component-library/Common/Icon'
import { ButtonStyled } from '@mobi/component-library/Common/V2/Buttons'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { Constants } from '@mobi/settings/constants'
import { hexColors } from '@mobi/settings/hexColors'
import { incrementQuickCounter } from '@core/Services/Analytics/helpers/quickCounter'
import {
  MIN_SUPPORTED_ANDROID_OS,
  UNSUPPORTED_APP_VERSION,
  MIN_SUPPORTED_IOS_OS,
} from '../constants'

export const ForceNativeAppUpdate: React.FC = () => {
  const [shouldForceAppUpdate, setShouldForceAppUpdate] = React.useState(false)
  const [isTrayOpen, setIsTrayOpen] = React.useState(true)

  React.useEffect(() => {
    if (!isNativeApp || !OSMajorVersion) return

    const minSupportedDeviceOS = isReactNativeAndroid
      ? MIN_SUPPORTED_ANDROID_OS
      : MIN_SUPPORTED_IOS_OS

    const isAppUpdateSupportedByDeviceOS = OSMajorVersion >= minSupportedDeviceOS

    if (isAppUpdateSupportedByDeviceOS && isNativeVersionAbove(UNSUPPORTED_APP_VERSION) === false) {
      setShouldForceAppUpdate(true)
      incrementQuickCounter('native-app-force-update-display')
    }
  }, [])

  if (!shouldForceAppUpdate) return null

  const urlToAppStore = isReactNativeAndroid
    ? Constants.GooglePlayStoreAppUrl
    : Constants.AppleAppStoreAppUrl

  return (
    <Tray
      isOpen={isTrayOpen}
      shouldCoverFullscreen
      dismissStatus='allowed'
      onDismiss={() => setIsTrayOpen(false)}
      headerTitle={null}
      footerComponent={
        <LinkButtonStyled
          size='md'
          color='primary'
          isFullWidth
          href={urlToAppStore}
          target='_blank'
          rel='noreferrer'
          style={{ maxWidth: '30rem', margin: '0 auto' }}
        >
          Update Now
        </LinkButtonStyled>
      }
    >
      <WrapperStyled>
        <div>
          <div>
            <Icon type='tabtouchLogoCompact' size='4.5rem' color={colors.white} />
          </div>
        </div>

        <div>
          <span>Version {AppVersion}</span>
          <h2>Update your TABtouch App</h2>
          <p>
            Your version of the TABtouch app is no longer supported. Please update now for improved
            security, stability & new features
          </p>
        </div>
      </WrapperStyled>
    </Tray>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.bigx2,
  paddingTop: '5rem',

  '> div:first-of-type': {
    display: 'flex',
    justifyContent: 'center',

    '> div': {
      padding: '1rem',
      backgroundColor: hexColors.blueDiamond,
      borderRadius: '1.3rem',
    },
  },

  '> div:last-of-type': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    maxWidth: '30rem',
    textAlign: 'center',
    fontFamily: font.family.primary,

    '> span, > p': {
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      fontWeight: font.weight.medium,
      color: colors.neutral[700],
    },

    '> h2': {
      textTransform: 'none',
      textShadow: 'none',
      color: colors.black,
      fontSize: font.size.xl3.fontSize,
      letterSpacing: font.size.xl3.letterSpacing,
      lineHeight: font.size.xl3.lineHeight,
      fontWeight: font.weight.bold,
      margin: 0,
      padding: 0,
    },

    '> p': {
      color: colors.neutral[900],
      margin: 0,
      padding: 0,
    },
  },
})

const LinkButtonStyled = styled(ButtonStyled)({
  textDecoration: 'none',
}).withComponent('a')
